import { useCallback, useState, useEffect, useMemo } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import Button from '@/common/components/Button';
import { Building, MoonIcon, SunIcon, Table2Icon } from 'lucide-react';
import { brasiliaCoordinates, loaderOptions, mapOptions } from './constants/constant';
import { useEstablishmentClusters } from './hooks/useEstablishmentClusters';
import { Cluster } from './types/cluster';
import { useNavigate } from 'react-router-dom';
import useEstablishmentCompany from '@/features/establishments/hooks/useEstablishmentCompany';
import { MarkerTooltip } from './components/MarkerTooltip';
import { EstablishmentClustersFilterProps } from './types/clusterFilters';
import ListHeader from '@/common/components/ListHeader';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import useDebounce from '@/common/hooks/useDebounce';
import { useEstablishmentMap } from './stores/useEstablishmentMap.store';
import EstablishmentMapHeader from './components/Header';
import EstablishmentMapCardList from './components/CardList';
import Switch from '@/common/components/Switch';
import { twMerge } from 'tailwind-merge';
import { isPositionVisible } from './utils/mapUtils';
import { singleMarker } from './constants/markers/singleMarker';
import { hoveredMarker } from './constants/markers/hoveredMarker';
import { stackedMarker } from './constants/markers/stackedMarker';

interface MapProps {
  setView: (view: "list" | "map") => void;
  view: "list" | "map";
  filterParams: {
    [key: string]: any;
  };
  setFilterParams: (mutationFn: (searchParams: any) => any) => void;
}

const Map = ({ setView, view, setFilterParams, filterParams }: MapProps) => {
  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);

  const {
    map,
    updateMap,
    center,
    updateCenter,
    colorScheme,
    updateColorScheme,
    hoveredMarkerId,
    updateHoveredMarkerId,
    boundsChangedOnCardClick,
    updateBoundsChangedOnCardClick,
    hoveredOnList,
    updateHoveredOnList,
    hoveredEstablishmentData,
    updateClusteredEstablishmentsIds,
    isAnimating,
  } = useEstablishmentMap();

  const navigate = useNavigate();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const {product, ...restParams} = filterParams

  const {
    data: clusteredEstablishments,
    dataUpdatedAt: clusteredEstablishmentUpdatedAt,
    error: establishmentClustersError = {} as any,
    refetch: refetchEstablishmentClusters,
  } = useEstablishmentClusters({ ...restParams, productCategoryId: product?.value, search } as EstablishmentClustersFilterProps);

  const identifiedClusters = useMemo(
    () => {
      const identifiedClusters = clusteredEstablishments?.map(cluster => {
        return {
          ...cluster,
          name: `marker-${cluster.latitude}-${cluster.longitude}-${cluster.establishment_count}`,
        };
      });

      updateClusteredEstablishmentsIds(clusteredEstablishments?.map((cluster) => cluster.establishment_company_id));

      return identifiedClusters;
    },
    [clusteredEstablishmentUpdatedAt],
  );

  const hoveredEstablishmentNotFoundInIdentifiedClusters = !!hoveredMarkerId && !identifiedClusters?.find((cluster) => cluster.establishment_company_id === hoveredMarkerId);
  const hoveredEstablishmentPositionIsVisible = hoveredEstablishmentNotFoundInIdentifiedClusters && map && isPositionVisible(map, {
    lat: Number(hoveredEstablishmentData?.general_information?.address.latitude),
    lng: Number(hoveredEstablishmentData?.general_information?.address.longitude),
  });

  const { data: establishment, isPending: isPendingEstablishmentData } = useEstablishmentCompany(hoveredMarkerId, !!hoveredMarkerId && !hoveredOnList && !hoveredEstablishmentNotFoundInIdentifiedClusters);

  const [hasCalledBoundsChange, setHasCalledBoundsChange] = useState(false);

  const handleGetInitialZoom = () => {
    if (center?.lat === brasiliaCoordinates.lat && center.lng === brasiliaCoordinates.lng) {
      return 6; // Case when the browser has no access to location
    } else {
      return 15;
    }
  };

  const handleMarkerClick = (cluster: Cluster) => {
    if (cluster.establishment_count > 1) {
      const newZoom = map?.getZoom()! + 1;
      map?.setZoom(newZoom);
      map?.setCenter({ lat: cluster.latitude, lng: cluster.longitude });
    } else {
      navigate(`/establishments/${cluster.establishment_company_id}`);
    }
  };

  const handleOnMouseOver = (cluster: Cluster & { name: string; }) => {
    if (cluster.establishment_count === 1) {
      updateHoveredOnList(false);
      updateHoveredMarkerId(cluster.establishment_company_id);
    }
  };

  const handleMapBoundsChange = useCallback((cameFromOnBound?: boolean, clearHoveredMarkerId = true) => {
    if (cameFromOnBound) {
      setHasCalledBoundsChange(true);
    }

    if (clearHoveredMarkerId) {
      updateBoundsChangedOnCardClick(false);
      updateHoveredMarkerId('');
    }

    const bounds = map?.getBounds();

    if (bounds) {
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();

      setFilterParams((prevParams) => ({
        ...prevParams,
        top_left_lat: northEast.lat(),
        top_left_lng: southWest.lng(),
        bottom_right_lat: southWest.lat(),
        bottom_right_lng: northEast.lng(),
      }));
    }
  }, [map, setFilterParams]);

  const setFilterBounds = useCallback((bounds: { top_left_lat: number; top_left_lng: number; bottom_right_lat: number; bottom_right_lng: number; }) => {
    setFilterParams((prevParams) => ({
      ...prevParams,
      top_left_lat: bounds.top_left_lat,
      top_left_lng: bounds.top_left_lng,
      bottom_right_lat: bounds.bottom_right_lat,
      bottom_right_lng: bounds.bottom_right_lng,
    }));
  }, [map, setFilterParams]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          updateCenter({ lat: latitude, lng: longitude });
        },
        (error) => {
          updateCenter(brasiliaCoordinates);
          console.error('Error getting location', error);
        }
      );
    } else {
      updateCenter(brasiliaCoordinates);
      console.error('Geolocation is not supported by this browser.');
    }
  }, []);

  if (!isLoaded || loadError || !center) {
    return (
      <>
        <ListHeader
          Icon={<Building className="w-10 h-10" strokeWidth={1.5} />}
          title="Rede credenciada"
        />
        <div className="flex w-full h-[calc(100vh-192px)] justify-center items-center">Carregando mapa...</div>
      </>
    );
  }

  return (
    <div className={twMerge('flex h-0 relative', view === 'map' && 'h-screen -m-8', view !== 'map' && 'overflow-hidden')}>
      <div className='absolute z-30 top-6 left-6'>
        <EstablishmentMapHeader />
      </div>

      <EstablishmentMapCardList
        setSearch={setSearch}
        setFilterBounds={setFilterBounds}
      />

      <div className='absolute z-20 right-8 top-6'>
        <Switch
          className='h-8 w-14 border px-[3px] data-[state=unchecked]:border-yellow-500 data-[state=unchecked]:bg-[#343233] data-[state=checked]:bg-[#343233] data-[state=checked]:border-primary-blue'
          thumbClassName='h-6 w-6 bg-yellow-500 transition-transform data-[state=checked]:translate-x-6 data-[state=checked]:bg-primary-blue'
          Icon={colorScheme === 'light' ? <SunIcon color='#343233' size={14} /> : <MoonIcon size={14} />}
          onCheckedChange={(checked) => updateColorScheme(checked ? 'dark' : 'light')}
          defaultChecked={colorScheme === 'dark'}
        />
      </div>

      <div className="flex flex-col gap-6 absolute z-20 top-[20px] left-[432px]">
        <Button
          name="switch_view"
          variant="neutral"
          className={twMerge(
            'text-sm py-4 bg-map-search-75 backdrop-blur hover:bg-map-search-100',
          )}
          onClick={() => setView('list')}
        >
          <Table2Icon size={18} strokeWidth={1.5} />

          Ver em tabela
        </Button>
      </div>

      <ErrorMessageWrapper
        className={view === 'map' ? '' : 'hidden'}
        statusCode={establishmentClustersError?.status}
        refetchFunction={refetchEstablishmentClusters}
      >
        <GoogleMap
          mapContainerStyle={{
            flex: 1,
          }}
          center={center}
          zoom={handleGetInitialZoom()}
          onLoad={(mapInstance) => updateMap(mapInstance)}
          options={{
            ...mapOptions({ colorScheme }),
            backgroundColor: colorScheme === 'dark' ? '#343233' : '#ECE7E4',
          }}
          onZoomChanged={() => {
            if (!isAnimating) {
              handleMapBoundsChange();
            }
          }}
          onDragEnd={() => handleMapBoundsChange()}
          onDragStart={() => updateHoveredMarkerId('')}
          onClick={() => updateHoveredMarkerId('')}
          onBoundsChanged={() => {
            if ((!hasCalledBoundsChange || boundsChangedOnCardClick) && view === 'map') {
              handleMapBoundsChange(true, !boundsChangedOnCardClick);
            }
          }}
        >
          {(hoveredEstablishmentNotFoundInIdentifiedClusters && hoveredEstablishmentData && hoveredEstablishmentPositionIsVisible) && (
            <div key={`marker-${hoveredEstablishmentData?.general_information?.address?.latitude}-${hoveredEstablishmentData?.general_information?.address?.longitude}-${1}-only`}>
              <Marker
                position={{
                  lat: Number(hoveredEstablishmentData?.general_information?.address?.latitude),
                  lng: Number(hoveredEstablishmentData?.general_information?.address?.longitude),
                }}
                icon={{
                  url: hoveredMarker({ colorScheme }),
                  anchor: new window.google.maps.Point(16, 50),
                }}
                zIndex={2}
              />

              <MarkerTooltip
                cluster={{
                  latitude: Number(hoveredEstablishmentData?.general_information?.address?.latitude),
                  longitude: Number(hoveredEstablishmentData?.general_information?.address?.longitude),
                }}
                establishment={hoveredEstablishmentData}
                isLoadingEstablishmentData={false}
              />
            </div>
          )}

          {identifiedClusters?.map((cluster) => (
            <div key={`${cluster.name}`}>
              <Marker
                position={{
                  lat: Number(cluster.latitude),
                  lng: Number(cluster.longitude),
                }}
                icon={{
                  url: hoveredMarkerId === cluster.establishment_company_id
                    ? hoveredMarker({ colorScheme })
                    : cluster.establishment_count > 1
                      ? stackedMarker({ colorScheme, count: cluster.establishment_count })
                      : singleMarker({ colorScheme }),
                }}
                zIndex={hoveredMarkerId === cluster.establishment_company_id ? 3 : 1}
                onClick={() => handleMarkerClick(cluster)}
                onMouseOver={() => handleOnMouseOver(cluster)}
                onMouseOut={(event) => {
                  if (!((event.domEvent as MouseEvent).relatedTarget as HTMLDivElement).closest('.establishment-map-tooltip')) {
                    updateHoveredMarkerId('');
                  }
                }}
              />

              {hoveredMarkerId === cluster.establishment_company_id && (
                <MarkerTooltip
                  cluster={cluster}
                  establishment={hoveredOnList ? hoveredEstablishmentData : establishment}
                  isLoadingEstablishmentData={hoveredOnList ? false : isPendingEstablishmentData}
                />
              )}
            </div>
          ))}
        </GoogleMap>
      </ErrorMessageWrapper>
    </div>
  );
};

export default Map;
